import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "../i18n/TranslationProvider";
import {
  translateOr,
  translateSymptom,
  translateTriggerText,
  toMomentLocale,
} from "../i18n/translateData";

import {
  PatientContext,
  ThemeContext,
} from "../lib/main-context";

import { parse } from "date-fns";
import moment from "moment";
import "moment/locale/es";
import "moment/locale/zh-cn";
import "moment/locale/id";
import { Col, Row, Card } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { Line } from "react-chartjs-2";
import { useHistory, useLocation } from "react-router-dom";

import theme from "../index.scss";
import ComponentWrapper from "./DashboardComponents/ComponentWrapper";
import LineChartOptions from "./DashboardComponents/LineChartOptions";
import ModalPdf from "./DashboardComponents/ModalPdf";
import DoughnutMedical from "./DoughnutMedical";
import DoughnutTrigger from "./DoughnutTrigger";
import FeelingBar from "./FeelingBar";
import MedicalBarChart from "./MedicalBarChart";
import SoapNotes from "./SoapNotes";
import SubmitButton from "./StyledComponents/SubmitButton";
import TriggerBarChart from "./TriggerBarChart";

import fillInDays from "../lib/fillInDates";
import getDaysAgo from "../lib/getDaysAgo";
import triggerWithSetVal from "../lib/triggerWithSetVal";

import { getAdditionalNote } from "../api/TBIRequests";
import { getPatientExercises } from "../api/adminRequests";

const ExpandedChart = ({ error, topSymptoms, data, retry, user }) => {
  const location = useLocation();
  const history = useHistory();
  const days = location.state?.days || 30;
  const expandedCategory = location.state?.expandedCategory; // Get the category to expand

  const { dark } = useContext(ThemeContext);
  const { patient } = useContext(PatientContext);
  const { t, language } = useTranslation();

  // Exercise reference data; each returns null when no translation key exists
  const tExerciseName = (name) => translateOr(t, `dashboard.exerciseNames.${name}`, null);
  const tExerciseCategory = (slug) => translateOr(t, `dashboard.exerciseCategories.${slug}`, null);

  // Set moment locale for day name translations in chart tooltips
  moment.locale(toMomentLocale(language));

  const translateSymptomLabel = (factor, subcategory) => {
    if (!factor || !subcategory) return factor;
    // Display only — dataset data keeps the English factor
    return translateSymptom(t, factor, subcategory);
  };

  const [assignedDays, setAssignedDays] = useState(days);
  const [additionalNotes, setAdditionalNotes] = useState([]);
  const [newData2, setNewData2] = useState({});
  const [triggerByDays, setTriggerByDays] = useState([]);
  const [showLineModal, setShowLineModal] = useState(false);
  const [startDate, setStartDate] = useState("");
  
  // State for Currently Assigned Exercises component
  const [providerViewAssignedExercises, setProviderViewAssignedExercises] = useState({});
  const [loadingProviderViewExercises, setLoadingProviderViewExercises] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Auto-expand the category passed from Dashboard
  useEffect(() => {
    if (expandedCategory) {
      setExpandedCategories({ [expandedCategory]: true });
    }
  }, [expandedCategory]);

  const medicalData = data
    ?.filter((obj) => obj.category !== "SDOH")
    .filter(
      (obj) =>
        parse(obj.symptom_date.slice(0, 10), "yyyy-MM-dd", new Date()) >
        getDaysAgo(assignedDays)
    );

  const triggerData = data
    ?.filter((obj) => obj.category == "SDOH")
    .filter(
      (obj) =>
        parse(obj.symptom_date.slice(0, 10), "yyyy-MM-dd", new Date()) >
        getDaysAgo(assignedDays)
    );

  const subcategories = [
    "all",
    ...Array.from(
      new Set(
        data
          .filter((obj) => obj.category === "medical")
          .filter(
            (obj) =>
              parse(obj.symptom_date.slice(0, 10), "yyyy-MM-dd", new Date()) >
              getDaysAgo(assignedDays)
          )
          .map((obj) => obj.subcategory)
      )
    ).sort((a, b) => a.localeCompare(b)),
  ];

  const [selectedSubcategories, setSelectedSubcategories] = useState("all");

  const colors = [
    "#ebce2b",
    "#1d1d1d",
    "#702c8c",
    "#db6917",
    "#96cde6",
    "#ba1c30",
    "#c0bd7f",
    "#7f7e80",
    "#5fa641",
    "#d485b2",
    "#4277b6",
    "#df8461",
    "#463397",
    "#e1a11a",
    "#91218c",
    "#e8e948",
    "#7e1510",
    "#92ae31",
    "#6f340d",
    "#d32b1e",
    "#2b3514",
  ];

  const subcategoryColors = {
    motor: "#b3e5fc",
    sensory: "#ffccbc",
    emotional: "#dab7ec",
    behavioral: "#ffdadb",
    cognitive: "#ffdcaf",
    immune: "#fff3c4",
    metabolic: "#cef3c4",
  };

  const handleLineModalClose = () => setShowLineModal(false);
  const handleLineModalShow = () => setShowLineModal(true);

  useEffect(() => {
    getAdditionalNote(patient.patientId)
      .then(({ data }) => {
        setAdditionalNotes(data);
      })
      .catch((e) => console.log("getAdditionalNotesError", e));
  }, []);

  // Fetch assigned exercises for provider to view what patient has
  useEffect(() => {
    let isMounted = true;
    
    // Only fetch for providers when a patient is selected
    if (user?.userType === "provider" && patient?.patientId) {
      setLoadingProviderViewExercises(true);
      
      // Pass patientId to get THAT patient's exercises
      getPatientExercises(patient.patientId)
        .then(({ data }) => {
          if (isMounted) {
            console.log("Provider received patient exercises:", data);
            setProviderViewAssignedExercises(data || {});
            setLoadingProviderViewExercises(false);
          }
        })
        .catch((error) => {
          console.error("Error fetching patient's assigned exercises:", error);
          if (isMounted) {
            setProviderViewAssignedExercises({});
            setLoadingProviderViewExercises(false);
          }
        });
    } else if (user?.userType === "provider" && !patient?.patientId) {
      // Clear when no patient selected
      setProviderViewAssignedExercises({});
      setLoadingProviderViewExercises(false);
    }

    return () => {
      isMounted = false;
    };
  }, [user?.userType, patient?.patientId]);

  let filterDataByDays = (dataset) => {
    const datasetConvertedToChartData = dataset.map((item) => {
      return {
        // CURRENT BUG, Must remove time stamp or labels do not work correctly
        x: item.symptom_date.slice(0, 10),
        y: item.severity,
        desc: translateSymptomLabel(item.description, item.subcategory), // was: item.description
      };
    });

    const filteredByDays = datasetConvertedToChartData.filter(
      (item) =>
        parse(item.x, "yyyy-MM-dd", new Date()) > getDaysAgo(assignedDays)
    );
    return filteredByDays;
  };

  let datasetArr =
    selectedSubcategories === "all"
      ? topSymptoms.map((symptom) => {
          return data.filter(({ factor }) => factor === symptom.factor);
        })
      : topSymptoms
          .filter(({ subcategory }) => subcategory === selectedSubcategories)
          .map((symptom) => {
            return data.filter(
              ({ factor, subcategory }) =>
                factor === symptom.factor &&
                subcategory === selectedSubcategories
            );
          });

  datasetArr.push(triggerByDays);

  let newData = {
    datasets: [],
    notes: additionalNotes,
  };
  let { datasets } = newData;

  datasetArr.forEach((dataset, index) => {
    const isLastDataset = index === datasetArr.length - 1;
    const datasetData = filterDataByDays(dataset);

    datasets.push({
      //label: isLastDataset ? t("dashboard.charts.triggerData.label") : topSymptoms[index].factor,
      label: isLastDataset ? t("dashboard.charts.triggerData.label") : translateSymptomLabel(
        topSymptoms[index]?.factor,
        topSymptoms[index]?.subcategory
      ),
      data: datasetData,
      pointStyle: isLastDataset ? "triangle" : undefined,
      type: isLastDataset ? "scatter" : undefined,
      backgroundColor: isLastDataset ? theme.primary : colors[index],
      borderColor: isLastDataset ? theme.primary : colors[index],
      borderWidth: 1,
      fill: isLastDataset ? false : undefined,
      hoverBackgroundColor: isLastDataset ? theme.primary : colors[index],
      hoverBorderColor: "white",
    });
  });

  let ChartDataset = fillInDays(datasets, assignedDays);
  newData.datasets = ChartDataset;

  let newDataC = {
    datasets: [],
    notes: additionalNotes,
  };
  // let { datasets } = newDataC;

  let ChartDatasetC = fillInDays(newDataC.datasets, assignedDays);
  newDataC.datasets = ChartDatasetC;

  useEffect(() => {
    datasetArr =
      selectedSubcategories === "all"
        ? topSymptoms.map((symptom) => {
            return data.filter(({ factor }) => factor === symptom.factor);
          })
        : topSymptoms
            .filter(({ subcategory }) => subcategory === selectedSubcategories)
            .map((symptom) => {
              return data.filter(
                ({ factor, subcategory }) =>
                  factor === symptom.factor &&
                  subcategory === selectedSubcategories
              );
            });

    const triggerList = triggerWithSetVal(data);
    const triggerByDays = triggerList.filter(
      (obj) =>
        parse(obj.symptom_date.slice(0, 10), "yyyy-MM-dd", new Date()) >
        getDaysAgo(assignedDays)
    );
    setTriggerByDays(triggerByDays);
    datasetArr.push(triggerList);

    datasets = [];
    datasetArr.forEach((dataset, index) => {
      const isLastDataset = index === datasetArr.length - 1;
      const datasetData = filterDataByDays(dataset);

      const filteredSymptoms =
        selectedSubcategories === "all"
          ? topSymptoms
          : topSymptoms.filter(
              ({ subcategory }) => subcategory === selectedSubcategories
            );
      datasets.push({
        //label: isLastDataset
        //  ? t("dashboard.charts.triggerData.label")
        //  : filteredSymptoms[index]?.factor,
        label: isLastDataset
        ? t("dashboard.charts.triggerData.label")
        : translateSymptomLabel(
            filteredSymptoms[index]?.factor,
            filteredSymptoms[index]?.subcategory
        ),
        data: datasetData,
        pointStyle: isLastDataset ? "triangle" : undefined,
        type: isLastDataset ? "scatter" : undefined,
        backgroundColor: isLastDataset ? theme.primary : colors[index],
        borderColor: isLastDataset ? theme.primary : colors[index],
        borderWidth: 1,
        fill: false,
        hoverBackgroundColor: isLastDataset ? theme.primary : colors[index],
        hoverBorderColor: "white",
      });
    });

    ChartDataset = fillInDays(datasets, assignedDays);
    const firstDay = getDaysAgo(assignedDays - 1);
    const formattedStartDate = firstDay?.toString().substring(0, 15);
    setStartDate(formattedStartDate);

    newData.datasets = ChartDataset;
    setNewData2(newData);

    ChartDatasetC = fillInDays(newDataC.datasets, assignedDays);

    newDataC.datasets = ChartDatasetC;
  }, [assignedDays, selectedSubcategories, language]);

  const extendedChartOption1 = {
    maintainAspectRatio: true,
    title: {
      display: true,
      text: `${assignedDays} ${t("dashboard.expandedChart.daysInReview")}`,
      fontFamily: "quicksand",
      fontStyle: "bold",
      fontSize: "20",
      fontColor: dark ? theme.darkModeText : theme.black,
    },
    legend: {
      labels: {
        fontColor: dark ? theme.darkModeText : theme.black,
        usePointStyle: true,
        boxWidth: 8,
      },
    },
  };

  // Helper to translate a desc string of comma-joined trigger factor names.
  // Mirrors the translateTriggerDesc logic in MedicalDetailsChart.js.
  // Uses TRIGGER_LIST (for trigger/SDOH desc values) sorted longest-first
  // to avoid partial matches (e.g. "Seasonal Spring, Summer" before "Spring").
  const translateTriggerDesc = (desc) => {
    // Longest-first replacement handled inside translateTriggerText
    return translateTriggerText(t, desc);
  };

  // Override tooltip label callback to translate trigger desc values
  LineChartOptions.tooltips.callbacks.label = (item, data) => {
    const point = data.datasets[item.datasetIndex].data[item.index];
    if (!point) return;

    const desc = point.desc || (point.y && point.y.desc);
    if (!desc) return;

    return translateTriggerDesc(desc);
  };

  // ===== Custom HTML legend (Chart.js 2.x) =====
  // Build one-item-per-line HTML legend. Long labels wrap naturally.
  function legendCallback(chart) {
    const text = [];
    text.push(
      '<ul class="chartjs-html-legend" style="list-style:none;margin:0;padding:0;">'
    );
    chart.data.datasets.forEach((dataset, i) => {
      const color = dataset.borderColor || dataset.backgroundColor || "#888";
      const dash = Array.isArray(dataset.borderDash)
        ? dataset.borderDash.join(",")
        : "";
      let markerSpan = "";
      if (dataset.label === t("dashboard.charts.triggerData.label")) {
        // Triangle marker using inline SVG, vertically centered using inline-flex and line-height:0
        markerSpan = `<span style="display:inline-flex;align-items:center;justify-content:center;width:14px;height:14px;margin-right:8px;line-height:0;">
            <svg width="14" height="14" viewBox="0 0 14 14" style="display:block;">
              <polygon points="7,2 13,12 1,12" style="fill:${color};stroke:${color};stroke-width:1" />
            </svg>
          </span>`;
      } else {
        markerSpan = `<span style="display:inline-block;width:12px;height:12px;margin-right:8px;background:${color};border-radius:50%;vertical-align:middle;line-height:0;${
          dash ? "border-bottom:2px dashed #000;" : ""
        }"></span>`;
      }
      text.push(
        `<li data-index="${i}" style="display:flex;align-items:center;margin:4px 0;white-space:normal;word-break:break-word;">
           ${markerSpan}
           <span class="legend-label" style="font-size:14px;font-weight:500;line-height:1.3;">${
             dataset.label || ""
           }</span>
         </li>`
      );
    });
    text.push("</ul>");
    return text.join("");
  }

  // Currently Assigned Exercises Component
  const CurrentlyAssignedExercises = () => {
    if (user?.userType !== "provider" || !patient?.patientId) {
      return null;
    }

    // Helper function to map category slugs to display names
    const getCategoryInfo = (categorySlug) => {
      // First, map individual exercise slugs to their parent categories
      const exerciseSlugToCategory = {
        'curlups': 'core-exercises',
        'lateralsideplank': 'core-exercises',
        'pronebridge': 'core-exercises',
        'pullups': 'core-exercises',
        'pushups': 'core-exercises',
        'supinebridge': 'core-exercises',
        
        'palmergrasp': 'primitive-reflexes',
        'rooting': 'primitive-reflexes',
        'snout': 'primitive-reflexes',
        'babinski': 'primitive-reflexes',
        'spinalgalant': 'primitive-reflexes',
        'atnr': 'primitive-reflexes',
        'stnr': 'primitive-reflexes',
        'moro': 'primitive-reflexes',
        'tlr': 'primitive-reflexes',
        
        'transcranial': 'sensory-equipment',
        'pons': 'sensory-equipment',
        'photobiomodulation-left': 'sensory-equipment',
        'photobiomodulation-right': 'sensory-equipment',
      };
      
      // Check if this is an individual exercise slug that needs to be mapped to a category
      const mappedCategory = exerciseSlugToCategory[categorySlug] || categorySlug;
      
      // Return just the slug - translation will happen at render time
      const categoryMap = {
        'primitive-reflexes': 'primitive-reflexes',
        'core-exercises': 'core-exercises',
        'balance': 'balance',
        'oculomotor': 'oculomotor',
        'vestibular': 'vestibular',
        'olfactory': 'olfactory',
        'rhythm-timing': 'rhythm-timing',
        'sensory-equipment': 'sensory-equipment',
        'metrotimer': 'metrotimer',
        'interactivemetronome': 'interactivemetronome'
      };
      
      if (categoryMap[mappedCategory]) {
        return categoryMap[mappedCategory];
      }
      
      return categorySlug;
    };

    // Helper function to get translated category name from slug
    const getTranslatedCategoryName = (categorySlug) => {
      // Handle specific categories that aren't translating properly
      // Maps slug/name variants to a key under dashboard.ui.exerciseCategory
      const manualTranslations = {
        'core': 'coreExercises',
        'Core': 'coreExercises',
        'sensory': 'sensoryEquipment',
        'Sensory': 'sensoryEquipment',
        'sensory-equipment': 'sensoryEquipment',
        'primitive-reflex': 'primitiveReflex',
        'Primitive Reflex': 'primitiveReflex',
        'rhythm-timing': 'rhythmAndTiming',
        'rhythm-and-timing': 'rhythmAndTiming',
        'Rhythm And Timing': 'rhythmAndTiming'
      };
      
      if (manualTranslations[categorySlug]) {
        return t(`dashboard.ui.exerciseCategory.${manualTranslations[categorySlug]}`);
      }
      
      if (tExerciseCategory(categorySlug)) {
        return tExerciseCategory(categorySlug);
      }
      
      // Format unknown categories: convert slug to title case
      const formatted = categorySlug
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      // MANUAL FIX: Check the formatted result against our manual translations
      if (manualTranslations[formatted]) {
        return t(`dashboard.ui.exerciseCategory.${manualTranslations[formatted]}`);
      }
      
      return formatted;
    };

    // Helper function to translate exercise names
    const translateExerciseName = (exerciseName) => {
      // Exercise names often come in format "Exercise Name Level X" or just "Exercise Name"
      // We need to extract the base name and translate it, then add back "Level X"
      
      // Check if the name ends with "Level X" pattern
      const levelMatch = exerciseName.match(/^(.+?)\s+Level\s+(\d+)$/i);
      
      if (levelMatch) {
        const baseName = levelMatch[1].trim();
        const levelNumber = levelMatch[2];
        
        // Try to translate the base name
        const translatedBase = tExerciseName(baseName) || baseName;
        return `${translatedBase} ${t("dashboard.exerciseDetails.level")} ${levelNumber}`;
      }
      
      // If no "Level X" pattern, try direct translation
      return tExerciseName(exerciseName) || exerciseName;
    };

    // Toggle category expansion
    const toggleCategory = (categoryName) => {
      setExpandedCategories(prev => ({
        ...prev,
        [categoryName]: !prev[categoryName]
      }));
    };

    // View All / Collapse All functionality
    const handleViewAll = () => {
      const allCategories = Object.keys(exercisesByCategory);
      const allExpanded = allCategories.every(cat => expandedCategories[cat]);
      
      if (allExpanded) {
        // If all are expanded, collapse all
        setExpandedCategories({});
      } else {
        // Otherwise, expand all
        const newExpanded = {};
        allCategories.forEach(cat => {
          newExpanded[cat] = true;
        });
        setExpandedCategories(newExpanded);
      }
    };

    // Organize exercises by category
    const exercisesByCategory = {};

    Object.entries(providerViewAssignedExercises).forEach(([categoryOrSlug, value]) => {
      if (value && typeof value === 'object') {
        if (value.exercise_name) {
          // Direct exercise at root level - treat it as its own category
          const categorySlug = getCategoryInfo(categoryOrSlug);
          if (!exercisesByCategory[categorySlug]) {
            exercisesByCategory[categorySlug] = { exercises: [] };
          }
          exercisesByCategory[categorySlug].exercises.push({
            name: value.exercise_name,
            level: value.exercise_level,
            slug: categoryOrSlug,
            progress: value.progress || 0,
            previousLevel: value.level_since_last_eval
          });
        } else {
          // Category with nested exercises
          const categorySlug = getCategoryInfo(categoryOrSlug);
          if (!exercisesByCategory[categorySlug]) {
            exercisesByCategory[categorySlug] = { exercises: [] };
          }
          
          Object.entries(value).forEach(([slug, exercise]) => {
            if (exercise && exercise.exercise_name) {
              exercisesByCategory[categorySlug].exercises.push({
                name: exercise.exercise_name,
                level: exercise.exercise_level,
                slug: slug,
                progress: exercise.progress || 0,
                previousLevel: exercise.level_since_last_eval
              });
            }
          });
        }
      }
    });

    const totalExercises = Object.values(exercisesByCategory).reduce(
      (sum, cat) => sum + cat.exercises.length, 
      0
    );

    if (loadingProviderViewExercises) {
      return (
        <Card className="mb-3 mt-3" style={{ 
          borderColor: dark ? '#28a745' : '#28a745',
          borderWidth: '2px'
        }}>
          <Card.Body>
            <h5 className="mb-3" style={{ color: '#28a745' }}>
              {t("dashboard.currentlyAssignedExercises")}
            </h5>
            <p className="text-muted">Loading assigned exercises...</p>
          </Card.Body>
        </Card>
      );
    }

    return (
      <Card className="mb-3 mt-3" style={{ 
        borderColor: dark ? '#28a745' : '#28a745',
        borderWidth: '2px'
      }}>
        <Card.Body>
          <h5 className="mb-3" style={{ color: '#28a745' }}>
            {t("dashboard.currentlyAssignedExercises")} ({totalExercises})
          </h5>
          {totalExercises === 0 ? (
            <p className="text-muted">
              No exercises have been assigned to this patient yet.
            </p>
          ) : (
            <>
              <div style={{ 
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                maxHeight: "400px", 
                overflowY: "auto" 
              }}>
                {Object.entries(exercisesByCategory).map(([categorySlug, categoryData]) => {
                  const isExpanded = expandedCategories[categorySlug] || false;
                  
                  return (
                    <div key={categorySlug} style={{ width: '100%' }}>
                      {/* Clickable oval button */}
                      <button
                        onClick={() => toggleCategory(categorySlug)}
                        style={{
                          display: 'inline-block',
                          backgroundColor: dark ? '#444' : '#f8f9fa',
                          color: dark ? '#fff' : '#333',
                          padding: '10px 24px',
                          borderRadius: '25px',
                          fontSize: '14px',
                          fontWeight: '600',
                          border: `1px solid ${dark ? '#666' : '#dee2e6'}`,
                          cursor: 'pointer',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                          transition: 'all 0.2s ease',
                          marginBottom: isExpanded ? '12px' : '0'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
                        }}
                      >
                        {getTranslatedCategoryName(categorySlug)} ({categoryData.exercises.length})
                      </button>
                      
                      {/* Collapsible exercise list */}
                      {isExpanded && (
                        <div style={{ 
                          marginLeft: '10px',
                          marginTop: '8px',
                          marginBottom: '15px',
                          animation: 'fadeIn 0.3s ease-in'
                        }}>
                          {categoryData.exercises.map((exercise, index) => (
                            <div 
                              key={index} 
                              style={{ 
                                display: 'flex', 
                                justifyContent: 'flex-start', 
                                alignItems: 'center',
                                padding: '10px 14px',
                                marginBottom: '8px',
                                backgroundColor: dark ? '#1f1f1f' : '#f8f9fa',
                                borderRadius: '8px',
                                border: `1px solid ${dark ? '#444' : '#dee2e6'}`,
                                transition: 'background-color 0.2s',
                                gap: '12px'
                              }}
                            >
                              <div>
                                <div style={{ 
                                  fontWeight: '500', 
                                  fontSize: '14px',
                                  color: dark ? '#fff' : '#333'
                                }}>
                                  {translateExerciseName(exercise.name)}
                                </div>
                                {exercise.progress !== undefined && (
                                  <div style={{ 
                                    fontSize: '11px',
                                    color: dark ? '#aaa' : '#666',
                                    marginTop: '4px'
                                  }}>
                                    {t("dashboard.exerciseDetails.progress")}: {Math.abs(exercise.progress)}/5
                                  </div>
                                )}
                              </div>
                              <div style={{ 
                                display: 'flex', 
                                flexDirection: 'column',
                                gap: '6px'
                              }}>
                                <span style={{ 
                                  fontSize: '12px',
                                  color: 'white',
                                  backgroundColor: '#6c757d',
                                  padding: '5px 14px',
                                  borderRadius: '15px',
                                  fontWeight: '600',
                                  whiteSpace: 'nowrap',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                }}>
                                  {t("dashboard.exerciseDetails.level")} {exercise.level}
                                </span>
                                {exercise.previousLevel !== undefined && exercise.previousLevel !== null && (
                                  <span style={{ 
                                    fontSize: '11px',
                                    color: '#333',
                                    backgroundColor: '#ffc107',
                                    padding: '4px 12px',
                                    borderRadius: '15px',
                                    fontWeight: '600',
                                    whiteSpace: 'nowrap',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                  }}>
                                    {t("dashboard.exerciseDetails.previousLevel")}: {exercise.previousLevel}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* View All button at bottom right */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end',
                marginTop: '12px'
              }}>
                <button
                  onClick={handleViewAll}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: dark ? '#444' : '#f8f9fa',
                    color: dark ? '#fff' : '#333',
                    border: `1px solid ${dark ? '#666' : '#dee2e6'}`,
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = dark ? '#555' : '#e9ecef';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = dark ? '#444' : '#f8f9fa';
                  }}
                >
                  {Object.keys(exercisesByCategory).every(cat => expandedCategories[cat]) 
                    ? t("dashboard.collapseAllButton")
                    : t("dashboard.viewAllButton")}
                </button>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    );
  };

  // Ref to the chart instance from react-chartjs-2
  const chartRef = useRef(null);

  // Render/update the HTML legend after chart renders/updates
  useEffect(() => {
    if (!chartRef.current || !chartRef.current.chartInstance) return;
    const chart = chartRef.current.chartInstance;
    const html = chart.generateLegend();
    const container = document.getElementById("line-legend");
    if (container) {
      container.innerHTML = html;
      // Click-to-toggle datasets (mimics built-in legend behavior)
      Array.from(container.querySelectorAll("li[data-index]")).forEach((li) => {
        li.addEventListener("click", () => {
          const idx = Number(li.getAttribute("data-index"));
          const meta = chart.getDatasetMeta(idx);
          meta.hidden =
            meta.hidden === null ? !chart.data.datasets[idx].hidden : null;
          chart.update();
        });
      });
    }
  }, [newData2, assignedDays, selectedSubcategories]);

  const colorOverRide = {
    scales: {
      xAxes: [
        {
          type: "time",
          time: {
            unit: "day",
            tooltipFormat: "dddd",
          },
          ticks: {
            fontColor: dark ? theme.darkModeText : theme.black,
          },
        },
      ],
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
            fontColor: dark ? theme.darkModeText : theme.black,
          },
          scaleLabel: {
            display: true,
            labelString: t("dashboard.intensityLevel"),
            fontColor: dark ? theme.darkModeText : theme.black,
          },
        },
      ],
    },
  };

  return (
    <>
      {error && (
        <ComponentWrapper>
          {t("dashboard.charts.error.0")}
          <SubmitButton onClick={retry}>
            {t("dashboard.charts.error.1")}
          </SubmitButton>
        </ComponentWrapper>
      )}
      <ComponentWrapper>
        <Row className="d-flex justify-content-around">
          <Form.Group controlId="formBasicSelect">
            <Form.Label>
              {t("dashboard.expandedChart.daysSince")} {startDate}{" "}
            </Form.Label>
            <Form.Control
              as="select"
              // value={date}
              defaultValue={assignedDays}
              onChange={(e) => {
                setAssignedDays(e.target.value);
              }}
            >
              <option
                value={5}
              >{`5 ${t("dashboard.expandedChart.daysInReview")}`}</option>
              <option
                value={30}
              >{`30 ${t("dashboard.expandedChart.daysInReview")}`}</option>
              {/* <option value={60}>{`60 ${t("dashboard.expandedChart.daysInReview")}`}</option> */}
              <option
                value={90}
              >{`90 ${t("dashboard.expandedChart.daysInReview")}`}</option>
              <option
                value={120}
              >{`120 ${t("dashboard.expandedChart.daysInReview")}`}</option>
            </Form.Control>
          </Form.Group>

          <SubmitButton
            onClick={() => {
              if (dark) {
                alert(t("dashboard.offDarkModeExport"));
              } else {
                handleLineModalShow();
              }
            }}
          >
            {t("dashboard.exportPdf")}
          </SubmitButton>
        </Row>
        <div className="row flex-column justify-content-center align-items-center">
          <div className="row" style={{ marginBottom: "10px" }}>
            {!patient.caregiverName &&
              t("dashboard.expandedChart.reportDetail.patient.0") +
                patient.firstName +
                " " +
                patient.lastName}
          </div>
        </div>
      </ComponentWrapper>
      <ModalPdf
        isReport={true}
        show={showLineModal}
        handleClose={handleLineModalClose}
        chart={[
          `trigger-doughnut`,
          `medical-doughnut`,
          `trigger-barChart`,
          `medical-barChart`,
          `feeling-bar`,
          `${assignedDays}day-lineChart`,
        ]}
        data={data}
        additionalNotes={additionalNotes}
        days={assignedDays}
      />
      <Row>
        <Col md={12}>
          <CurrentlyAssignedExercises />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <SoapNotes />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <ComponentWrapper>
            <div className={`feeling-bar`}>
              <FeelingBar
                data={medicalData}
                userName={
                  patient.caregiverName
                    ? patient.caregiverName
                    : patient.firstName
                }
              />
            </div>
          </ComponentWrapper>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <ComponentWrapper>
            <div className={`trigger-doughnut`}>
              <DoughnutTrigger
                data={triggerData}
                days={assignedDays}
                userName={
                  patient.caregiverName
                    ? patient.caregiverName
                    : patient.firstName
                }
              />
            </div>
          </ComponentWrapper>
        </Col>
        <Col md={6}>
          <ComponentWrapper>
            <div className={`medical-doughnut`}>
              <DoughnutMedical
                data={medicalData}
                days={assignedDays}
                userName={
                  patient.caregiverName
                    ? patient.caregiverName
                    : patient.firstName
                }
              />
            </div>
          </ComponentWrapper>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <ComponentWrapper>
            <div className={`trigger-barChart`}>
              <TriggerBarChart
                data={triggerByDays}
                userName={
                  patient.caregiverName
                    ? patient.caregiverName
                    : patient.firstName
                }
              />
            </div>
          </ComponentWrapper>
        </Col>
        <Col md={6}>
          <ComponentWrapper>
            <div className={`medical-barChart`}>
              <MedicalBarChart
                data={medicalData}
                userName={
                  patient.caregiverName
                    ? patient.caregiverName
                    : patient.firstName
                }
              />
            </div>
          </ComponentWrapper>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <ComponentWrapper>
            <div className={`${assignedDays}day-lineChart`}>
              <Line
                key={language}
                ref={chartRef}
                data={newData2}
                options={{
                  ...LineChartOptions,
                  ...extendedChartOption1,
                  ...colorOverRide,
                  /*
                  legend: {
                    position: "bottom",
                    labels: {
                      usePointStyle: true,
                      boxWidth: 8,
                      generateLabels: function (chart) {
                        return chart.data.datasets.map((dataset, index) => {
                          return {
                            text: dataset.label,
                            fullText: dataset.label,
                            fillStyle: dataset.backgroundColor,
                            hidden: !chart.isDatasetVisible(index),
                            datasetIndex: index,
                            pointStyle:
                              dataset.label === t("dashboard.charts.triggerData.label")
                                ? "triangle"
                                : "circle",
                          };
                        });
                      },
                    },
                  },
                  */
                  legend: { display: false },
                  legendCallback: legendCallback,
                }}
              />
              <div
                id="line-legend"
                style={{
                  marginTop: "10px",
                  maxHeight: "200px",
                  overflowY: "auto",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "8px 10px",
                  background: "#fafafa",
                }}
              />
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                gap: "10px",
                marginBottom: "1rem",
              }}
            >
              {subcategories.map((subcategory) => (
                <div
                  key={subcategory}
                  style={{
                    fontFamily: "quicksand",
                    fontStyle: "bold",
                    fontSize: "20",
                    fontColor: dark ? theme.darkModeText : theme.black,
                    backgroundColor:
                      subcategoryColors[subcategory] || "#e6e6e6",
                    borderRadius: "8px",
                    padding: "10px",
                    textAlign: "center",
                    cursor: "pointer",
                    border:
                      selectedSubcategories === subcategory
                        ? "3px solid #3DB1A6"
                        : "1px solid #ddd",
                    marginTop: "0.5rem",
                  }}
                  onClick={() => setSelectedSubcategories(subcategory)}
                >
                  {subcategory === "all"
                    ? t("dashboard.ui.all")
                    : translateOr(t, `dashboard.symptomCategory.${subcategory.charAt(0).toUpperCase() + subcategory.slice(1)}`, subcategory.charAt(0).toUpperCase() + subcategory.slice(1))
                  }
                </div>
              ))}
            </div>
          </ComponentWrapper>
        </Col>
      </Row>
      <SubmitButton onClick={() => history.push("/")}>
        {t("dashboard.goBack")}
      </SubmitButton>
    </>
  );
};

export default ExpandedChart;